import { Box, Container, Heading, Text, Button, Flex, VStack, Stack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Rocket as RocketLaunch, DollarSign } from 'lucide-react';
import CTAButton from './CTAButton';
import StarField from './StarField';
import { useAnalytics } from '../hooks/useAnalytics';

const MotionBox = motion(Box);

const BackgroundVideo = () => (
  <Box
    position='absolute'
    top='0'
    left='0'
    right='0'
    bottom='0'
    overflow='hidden'
    zIndex='0'
    sx={{
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, rgba(2, 8, 20, 0.7), rgba(2, 8, 20, 0.85))',
        zIndex: 1,
      },
    }}>
    <Box
      as='video'
      position='absolute'
      top='50%'
      left='50%'
      minWidth='100%'
      minHeight='100%'
      width='auto'
      height='auto'
      transform='translate(-50%, -50%)'
      objectFit='cover'
      autoPlay
      muted
      loop
      playsInline
      sx={{
        filter: 'brightness(0.7)',
      }}>
      <source src='/space-video.mp4' type='video/mp4' />
      Your browser does not support the video tag.
    </Box>
  </Box>
);

const Hero = () => {
  const { trackEvent } = useAnalytics();

  const handleExploreClick = () => {
    trackEvent('hero_cta_clicked', {
      button: 'explore_vision',
      section: 'hero',
    });

    const element = document.getElementById('products');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePartnerClick = () => {
    trackEvent('hero_cta_clicked', {
      button: 'partner_interest',
      section: 'hero',
    });

    const element = document.getElementById('funding');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id='home'
      as='section'
      aria-label='Welcome to GallifreyAI'
      minH={{ base: '90vh', md: '80vh' }}
      position='relative'
      display='flex'
      alignItems='center'
      className='parallax-section'
      overflow='hidden'
      pt={{ base: '80px', md: '0' }}
      onMouseEnter={() => trackEvent('section_viewed', { section: 'hero' })}>
      <BackgroundVideo />
      <StarField />
      <Container maxW='1200px' position='relative' zIndex='2' px={{ base: 4, md: 6 }}>
        <Flex direction='column' align='flex-start' maxW='800px'>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>
            <Heading
              as='h1'
              size={{ base: 'xl', md: '2xl' }}
              mb={6}
              letterSpacing='wider'
              lineHeight='1.2'
              color='brand.light'
              className='cosmic-card'>
              AI-Powered Developer Tools for Inclusive Innovation
            </Heading>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}>
            <Text fontSize={{ base: 'lg', md: 'xl' }} mb={8} color='gray.300' maxW='600px'>
              We're building AI-powered tools to streamline development processes, enabling teams to
              focus on what matters most: creating exceptional software. Our solutions adapt to
              different ways of thinking and working.
            </Text>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            width='100%'>
            <VStack align='stretch' spacing={4}>
              <Stack direction={{ base: 'column', md: 'row' }} spacing={4} width='100%'>
                <CTAButton
                  text='Explore Our Vision'
                  leftIcon={<RocketLaunch size={20} />}
                  onClick={handleExploreClick}
                  aria-label='Learn about our products'
                  width={{ base: '100%', md: 'auto' }}
                />
                <Button
                  variant='outline'
                  size='lg'
                  fontSize='md'
                  px={8}
                  leftIcon={<DollarSign size={20} />}
                  color='brand.accent'
                  borderColor='brand.accent'
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(70, 255, 255, 0.3)',
                    bg: 'rgba(70, 255, 255, 0.1)',
                  }}
                  onClick={handlePartnerClick}
                  aria-label='Partner with us'
                  width={{ base: '100%', md: 'auto' }}>
                  Partner With Us
                </Button>
              </Stack>
              <Box
                p={4}
                bg='rgba(11, 61, 145, 0.3)'
                borderRadius='xl'
                borderWidth='1px'
                borderColor='brand.accent'
                className='cosmic-card'
                role='complementary'>
                <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }}>
                  <Text as='span' color='brand.accent' fontWeight='bold'>
                    Early Access Available:{' '}
                  </Text>
                  Join our beta program and help shape the future of development tools.
                </Text>
              </Box>
            </VStack>
          </MotionBox>
        </Flex>
      </Container>

      <Box
        position='absolute'
        bottom='0'
        left='0'
        right='0'
        height='150px'
        bgGradient='linear(to-t, brand.dark, transparent)'
        aria-hidden='true'
      />
    </Box>
  );
};

export default Hero;
